import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { Modal } from "@icg360/design-system";

import { EasyPayEnrollmentModal } from "components/easypay-enrollment-modal";
import { ExternalHeader } from "components/external-header";
import { PaperlessModal } from "components/paperless-modal";
import { ENROLLMENT_STATUS, ORIGINS, PAYMENT_METHOD_CODE } from "consts";
import {
  useClaimsList,
  usePoliciesList,
  usePolicyDetail,
  usePolicyPlatformId,
  useUser,
} from "data/ss-data-hooks";
import {
  getCookie,
  getFullstorySession,
  getPaymentMethodDetails,
  identifyFullstoryUser,
  identifyUserMixpanel,
  isEasyPayEnrolledOrPending,
  isPaperlessDocumentEligible,
  setCookie,
  setMixpanelProfileProperties,
  setMixpanelSuperProperties,
  setSentryFeatFlags,
  setSentryLinks,
  setSentryTag,
  setSentryUser,
  trackEvent,
  urlSearchParamsObject,
  useFlags,
} from "utils";

import { trackClaimsInfo } from "./utils/track-claims-info";

type ModalName = "paperless" | "easypay" | "invalid-policy";

export const AuthApp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const featureFlags = useFlags();
  const [invalidPolicy, setInvalidPolicy] = useState("");
  const [paymentMethod, setPaymentMethod] = useState();
  const [curModal, setCurModal] = useState<ModalName | null>(null);

  const userInfo = useUser();
  const selectedPolicyId = usePolicyPlatformId();
  const { data: policies } = usePoliciesList();
  const { data } = usePolicyDetail();
  const isPolicyDataReady = !!selectedPolicyId && data;

  useEffect(() => {
    if (!data?.userBilling) {
      return;
    }
    const curPaymentMethod = getPaymentMethodDetails(
      data.userBilling.accounting?.paymentPlan?.planType?.toLowerCase(),
    ).paymentMethod;
    setPaymentMethod(curPaymentMethod);
    setMixpanelSuperProperties({ paymentPlan: curPaymentMethod });
    setMixpanelProfileProperties({
      paymentPlan: curPaymentMethod,
    });
  }, [data?.userBilling]);

  useEffect(() => {
    if (!data?.userPolicyProperties) {
      return;
    }

    setMixpanelSuperProperties({
      carrier: data.userPolicyProperties.carrierName,
      policyState: data.userPolicyProperties.policyState,
    });
  }, [data?.userPolicyProperties]);

  useEffect(() => {
    if (data?.userDetails) {
      const { currentTerm, policyStatus, propertyAddress, insuredPreferences } =
        data.userDetails;

      setMixpanelSuperProperties({
        policyStatus,
        agencyLocationCode: currentTerm?.agencyLocationCode,
        propertyState: propertyAddress?.state,
        zipCode: propertyAddress?.zip,
      });

      setMixpanelProfileProperties({
        agencyLocationCode: currentTerm?.agencyLocationCode,
        paperlessDocumentsEnrollment:
          insuredPreferences?.paperlessDocumentEnrollmentStatus ||
          ENROLLMENT_STATUS.NOT_ENROLLED,
        paperlessDocumentsEmail:
          insuredPreferences?.opInsuredEmailAddressDocuments || "",
        paperlessBillingEnrollment:
          insuredPreferences?.paperlessBillingEnrollmentStatus ||
          ENROLLMENT_STATUS.NOT_ENROLLED,
        paperlessBillingEmail:
          insuredPreferences?.opInsuredEmailAddressBilling || "",
      });
    }
  }, [data?.userDetails]);

  //********** START DATA FETCHING **********//
  const { isLoading: isClaimsLoading, data: claimsList } = useClaimsList();

  useEffect(() => {
    (async () => {
      if (!isClaimsLoading) {
        trackClaimsInfo(claimsList);
      }
    })();
  }, [claimsList, isClaimsLoading]);

  const parsedQS = urlSearchParamsObject();
  const isCommercial = data?.userDetails.isCommercial;
  const isEasyPayEnrolled = isEasyPayEnrolledOrPending(
    data?.userBilling,
    data?.userDetails,
  );

  // Populate Qualtrics user data.
  useEffect(() => {
    if (policies) {
      window.SURVEY_USER_FIRST_NAME = userInfo?.firstName;
      window.SURVEY_USER_LAST_NAME = userInfo?.lastName;
      window.SURVEY_USER_EMAIL = userInfo?.email;
      window.SURVEY_USER_POLICY_NUMBERS = policies
        .map((p) => p?.platformPolicyId)
        .join(", ");
    }
  }, [policies, userInfo]);

  // Identify user to third-party systems
  useEffect(() => {
    if (!userInfo || !data?.userDetails || !selectedPolicyId || !featureFlags) {
      return;
    }

    setSentryUser({
      userId: userInfo.userId ?? "",
      email: userInfo.email ?? "",
    });
    setSentryLinks({
      FullStory: getFullstorySession() ?? "",
    });
    setSentryFeatFlags(featureFlags);
    setSentryTag("policy", selectedPolicyId);

    identifyFullstoryUser({
      userId: userInfo.userId ?? "",
      email: userInfo.email ?? "",
      displayName: `${userInfo.firstName} ${userInfo.lastName}`,
    });
    identifyUserMixpanel({
      email: userInfo.email,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      userId: userInfo.userId,
    });

    // Set super properties
    setMixpanelSuperProperties({
      policyNumber: selectedPolicyId,
      isBookRoll: data?.userDetails.isBookRoll,
    });
  }, [featureFlags, data?.userDetails, selectedPolicyId, userInfo]);

  useEffect(() => {
    if (!isPolicyDataReady) {
      return;
    }

    const email = userInfo?.email?.toLowerCase() ?? "";

    setCookie("mss-username", btoa(email));
  }, [
    isPolicyDataReady,
    selectedPolicyId,
    data?.userDetails,
    userInfo,
    data?.userPolicyProperties,
  ]);

  useEffect(() => {
    const initializeModals = () => {
      if (parsedQS.origin === ORIGINS.DIGITAL_INVOICE) {
        // if the policyNumber were valid, it would have been removed from the qs (look in useAuthAppData)
        if (parsedQS.policyNumber) {
          trackEvent("Error Modal Displayed (policy not found)", {
            policyNumber: parsedQS.policyNumber,
            errorMessage: "Policy is not linked to the account",
          });
          setInvalidPolicy(parsedQS.policyNumber);
          setCurModal("invalid-policy");
          navigate("/my/overview");
        } else if (isEasyPayEnrolled) {
          trackEvent("Error Modal Displayed (easypay enrolled)", {
            policyNumber: selectedPolicyId,
            errorMessage: "Policy is already enrolled in EasyPay",
          });
          setCurModal("easypay");
        }
        navigate(location.pathname);
      } else {
        if (
          getCookie("mss-easypay-modal-dismissed") === undefined &&
          paymentMethod !== PAYMENT_METHOD_CODE.INVOICE &&
          !isEasyPayEnrolled
        ) {
          setCurModal("easypay");
        } else if (
          getCookie("mss-paperless-modal-dismissed") === undefined &&
          isPaperlessDocumentEligible(data?.userDetails)
        ) {
          setCurModal("paperless");
        }
      }
    };

    if (
      !isPolicyDataReady ||
      !paymentMethod ||
      location.pathname !== "/my/overview"
    ) {
      return;
    }
    initializeModals();
  }, [
    isPolicyDataReady,
    paymentMethod,
    location.pathname,
    parsedQS.origin,
    parsedQS.policyNumber,
    isEasyPayEnrolled,
    navigate,
    selectedPolicyId,
    data?.userDetails,
  ]);

  return (
    <>
      <ExternalHeader returnUrlParsed />
      <Outlet />
      {isPolicyDataReady && (
        <PaperlessModal
          show={curModal === "paperless" && !isCommercial}
          onClose={() => {
            setCurModal(null);
            setCookie("mss-paperless-modal-dismissed", true);
          }}
          documentsEmailAddress={
            data?.userDetails?.insuredPreferences
              ?.opInsuredEmailAddressDocuments ??
            data?.userDetails?.insuredPreferences?.opInsuredEmailAddress ??
            ""
          }
          userDetails={data?.userDetails}
          userBilling={data?.userBilling}
          policyId={selectedPolicyId!}
        />
      )}
      {paymentMethod !== PAYMENT_METHOD_CODE.INVOICE && (
        <EasyPayEnrollmentModal
          show={curModal === "easypay"}
          onClose={() => setCurModal(null)}
        />
      )}
      <Modal
        open={curModal === "invalid-policy"}
        heading="This policy is not in your account."
        onClose={() => setCurModal(null)}
        primaryAction="Add new policy"
        onPrimaryAction={() => {
          setCurModal(null);
          navigate("/my/settings/policies");
        }}
      >
        {`Policy # ${invalidPolicy} is not currently linked to your online account. To continue EasyPay enrollment, switch to another account or add new policy to your account.`}
      </Modal>
    </>
  );
};
